import React from 'react';
import { Trash2 } from 'lucide-react';
import { GasType, GasTypeUpdate } from '../../types/gasTypes';
import Input from './common/Input';
import Button from './common/Button';

/**
 * Props interface for the GasTypeInput component
 */
interface GasTypeInputProps {
  /** Gas type data */
  gasType: GasType;
  /** Whether this gas type can be removed */
  canRemove: boolean;
  /** Handler for updating gas type fields */
  onUpdate: (id: number, field: keyof GasTypeUpdate, value: string | number) => void;
  /** Handler for removing gas type */
  onRemove: (id: number) => void;
}

/**
 * GasTypeInput Component
 * Renders input fields for a single gas type including name, alcohol percentage, octane rating, and price
 */
const GasTypeInput: React.FC<GasTypeInputProps> = ({
  gasType,
  canRemove,
  onUpdate,
  onRemove
}) => {
  // Handle input changes
  const handleNameChange = (value: string) => {
    onUpdate(gasType.id, 'name', value);
  };

  const handleAlcoholChange = (value: string) => {
    onUpdate(gasType.id, 'alcoholPercent', value);
  };

  const handleOctaneChange = (value: string) => {
    onUpdate(gasType.id, 'octaneRating', value);
  };

  const handlePriceChange = (value: string) => {
    onUpdate(gasType.id, 'pricePerLiter', value);
  };

  const handleRemove = () => {
    onRemove(gasType.id);
  };

  return (
    <div className="card-compact">
      <div className="space-y-4">
        {/* Gas Name Input */}
        <Input
          value={gasType.name}
          onChange={handleNameChange}
          placeholder="Gas name"
          className="font-semibold text-lg"
        />

        {/* Gas Properties Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3">
          {/* Alcohol Percentage */}
          <Input
            label="Alcohol %"
            type="number"
            value={gasType.alcoholPercent}
            onChange={handleAlcoholChange}
            min="0"
            max="100"
            step="0.1"
          />

          {/* Octane Rating */}
          <Input
            label="Octane"
            type="number"
            value={gasType.octaneRating}
            onChange={handleOctaneChange}
            min="80"
            max="120"
            step="0.1"
          />

          {/* Price per Liter */}
          <Input
            label="Price €/L"
            type="number"
            value={gasType.pricePerLiter}
            onChange={handlePriceChange}
            min="0"
            step="0.01"
          />
        </div>

        {/* Remove Button (only show if can remove) */}
        {canRemove && (
          <Button
            variant="danger"
            onClick={handleRemove}
            icon={Trash2}
            fullWidth
            size="medium"
            className="mt-2"
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default GasTypeInput;